import { httpsCallable } from 'firebase/functions';
import type {
  BillingRegisterCardRequest,
  BillingRegisterCardResponse,
} from '@kawai/shared';
import { functions } from '../firebase.js';

const billingRegisterCardCallable = httpsCallable<
  BillingRegisterCardRequest,
  BillingRegisterCardResponse
>(functions, 'billingRegisterCard');

/**
 * TP-20: `billingRegisterCard` Callable の薄いラッパ（テナント自身がカードを登録）。
 * フロントで payjp.js が 3DS 検証済みトークン（tok_…）を作り、それをサーバに渡す。
 * Functions 側で role/tenant 検証（tenant-admin は自テナントのみ）+ PAY.JP Customer 作成/カード保存 + audit を行う。
 */
export async function callBillingRegisterCard(
  input: BillingRegisterCardRequest,
): Promise<BillingRegisterCardResponse> {
  const res = await billingRegisterCardCallable(input);
  return res.data;
}

interface FunctionsErrorLike {
  code: string;
  message?: string;
}

function isFunctionsErrorLike(err: unknown): err is FunctionsErrorLike {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string' &&
    (err as { code: string }).code.startsWith('functions/')
  );
}

/** `billingRegisterCard` のエラーをテナント向け日本語メッセージに変換。 */
export function billingRegisterCardErrorMessage(err: unknown): string {
  if (isFunctionsErrorLike(err)) {
    switch (err.code) {
      case 'functions/permission-denied':
        return 'カードを登録する権限がありません（テナント管理者のみ）。';
      case 'functions/unauthenticated':
        return 'セッションが切れました。再ログインしてください。';
      case 'functions/failed-precondition':
        return `3Dセキュア認証が完了していません：${err.message ?? ''}`;
      case 'functions/not-found':
        return 'テナント情報が見つかりません。';
      case 'functions/invalid-argument':
        return `入力値に誤りがあります：${err.message ?? ''}`;
      case 'functions/aborted':
        return '登録が混み合っています。少し待って再度お試しください。';
      default:
        return `カード登録に失敗しました（${err.code}）。`;
    }
  }
  if (err instanceof Error) {
    return `カード登録に失敗しました：${err.message}`;
  }
  return 'カード登録に失敗しました。';
}
